/**
 * Admin functions for managing map coordinates
 * Used in the Map Tracer tool for updating unit polygon coordinates
 */
import { supabase } from '@/integrations/supabase/client';
import { trackEvent } from '@/lib/analytics';
import type { PropertyUnit } from '@/lib/units';
import type { MapCoordinates } from '@/types/map';
import type { Json } from '@/integrations/supabase/types';

export interface SiteMapPolygon {
  id: string;
  block: 'A' | 'B';
  polygon_number: number;
  map_coordinates: MapCoordinates;
  created_at: string;
  updated_at: string;
}

export interface UnitMapCoordinate {
  unit_number: string;
  unit_id: string;
  block: 'A' | 'B';
  floor: 'GF' | 'second' | 'third' | 'fourth';
  map_coordinates: MapCoordinates | null;
  map_element_id: string | null;
}

/**
 * Update map coordinates for a single unit
 */
export async function updateUnitMapCoordinates(
  unitId: string,
  coordinates: MapCoordinates,
  mapElementId?: string
): Promise<{ success: boolean; error?: string; data?: PropertyUnit }> {
  try {
    const updates: { map_coordinates: any; map_element_id?: string } = {
      map_coordinates: coordinates as any,
    };

    if (mapElementId) {
      updates.map_element_id = mapElementId;
    }

    const { data, error } = await supabase
      .from('property_units')
      .update(updates)
      .eq('id', unitId)
      .select()
      .single();

    if (error) {
      console.error('Error updating unit map coordinates:', error);
      return { success: false, error: error.message };
    }

    trackEvent('admin_map_coordinates_updated', {
      unit_id: unitId,
      unit_number: data.unit_number,
      point_count: coordinates.points?.length || 0,
    });

    return { success: true, data: data as PropertyUnit };
  } catch (error: unknown) {
    console.error('Error updating unit map coordinates:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: errorMessage };
  }
}

/**
 * Update map coordinates for a unit by unit number
 * Updates all floors for that unit number
 */
export async function updateUnitMapCoordinatesByNumber(
  unitNumber: string,
  coordinates: MapCoordinates,
  mapElementId?: string
): Promise<{ success: boolean; error?: string; updatedCount?: number }> {
  try {
    const updates: { map_coordinates: any; map_element_id?: string } = {
      map_coordinates: coordinates as any,
    };

    if (mapElementId) {
      updates.map_element_id = mapElementId;
    }

    const { data, error } = await supabase
      .from('property_units')
      .update(updates)
      .eq('unit_number', unitNumber)
      .select('id');

    if (error) {
      console.error('Error updating unit map coordinates:', error);
      return { success: false, error: error.message };
    }

    trackEvent('admin_map_coordinates_updated_by_number', {
      unit_number: unitNumber,
      updated_count: data?.length || 0,
      point_count: coordinates.points?.length || 0,
    });

    return { success: true, updatedCount: data?.length || 0 };
  } catch (error: unknown) {
    console.error('Error updating unit map coordinates:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: errorMessage };
  }
}

/**
 * Bulk update map coordinates for multiple units
 */
export async function bulkUpdateMapCoordinates(
  updates: Array<{ unitId: string; coordinates: MapCoordinates; mapElementId?: string }>
): Promise<{ success: boolean; error?: string; updatedCount?: number }> {
  try {
    // Update each unit individually to handle errors gracefully
    let successCount = 0;
    let errorCount = 0;
    const errors: string[] = [];

    for (const update of updates) {
      const result = await updateUnitMapCoordinates(
        update.unitId,
        update.coordinates,
        update.mapElementId
      );
      if (result.success) {
        successCount++;
      } else {
        errorCount++;
        errors.push(result.error || 'Unknown error');
      }
    }

    trackEvent('admin_bulk_map_coordinates_updated', {
      total: updates.length,
      success_count: successCount,
      error_count: errorCount,
    });

    if (errorCount > 0) {
      return {
        success: false,
        error: `${errorCount} units failed to update: ${errors.join('; ')}`,
        updatedCount: successCount,
      };
    }

    return { success: true, updatedCount: successCount };
  } catch (error: unknown) {
    console.error('Error bulk updating map coordinates:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: errorMessage };
  }
}

/**
 * Get all units with their map coordinates
 */
export async function getUnitsWithMapCoordinates(): Promise<{
  data: UnitMapCoordinate[] | null;
  error: string | null;
}> {
  try {
    const { data, error } = await supabase
      .from('property_units')
      .select('id, unit_number, block, floor, map_coordinates, map_element_id')
      .order('unit_number')
      .order('floor');

    if (error) {
      console.error('Error fetching units with map coordinates:', error);
      return { data: null, error: error.message };
    }

    const units: UnitMapCoordinate[] = (data || []).map((unit) => ({
      unit_number: unit.unit_number,
      unit_id: unit.id,
      block: unit.block,
      floor: unit.floor,
      map_coordinates: (unit.map_coordinates as unknown) as MapCoordinates | null,
      map_element_id: unit.map_element_id,
    }));

    return { data: units, error: null };
  } catch (error: unknown) {
    console.error('Error fetching units with map coordinates:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { data: null, error: errorMessage };
  }
}

/**
 * Export all map coordinates to JSON format
 */
export async function exportMapCoordinates(): Promise<{
  data: string | null;
  error: string | null;
}> {
  try {
    const { data, error } = await getUnitsWithMapCoordinates();

    if (error || !data) {
      return { data: null, error: error || 'Failed to fetch units' };
    }

    const exportData = {
      exported_at: new Date().toISOString(),
      total_units: data.length,
      units: data.map((unit) => ({
        unit_number: unit.unit_number,
        block: unit.block,
        floor: unit.floor,
        map_element_id: unit.map_element_id,
        map_coordinates: unit.map_coordinates,
      })),
    };

    return { data: JSON.stringify(exportData, null, 2), error: null };
  } catch (error: unknown) {
    console.error('Error exporting map coordinates:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { data: null, error: errorMessage };
  }
}

/**
 * Export map coordinates to CSV format
 */
export async function exportMapCoordinatesCSV(): Promise<{
  data: string | null;
  error: string | null;
}> {
  try {
    const { data, error } = await getUnitsWithMapCoordinates();

    if (error || !data) {
      return { data: null, error: error || 'Failed to fetch units' };
    }

    const csvRows = [
      'unit_number,block,floor,map_element_id,polygon_points',
      ...data.map((unit) => {
        const points = unit.map_coordinates?.points
          ? JSON.stringify(unit.map_coordinates.points)
          : '';
        return `${unit.unit_number},${unit.block},${unit.floor},${unit.map_element_id || ''},"${points}"`;
      }),
    ];

    return { data: csvRows.join('\n'), error: null };
  } catch (error: unknown) {
    console.error('Error exporting map coordinates to CSV:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { data: null, error: errorMessage };
  }
}

/**
 * Import map coordinates from JSON format
 */
export async function importMapCoordinates(
  jsonData: string
): Promise<{ success: boolean; error?: string; importedCount?: number }> {
  try {
    const parsed = JSON.parse(jsonData);

    if (!parsed.units || !Array.isArray(parsed.units)) {
      return { success: false, error: 'Invalid JSON format: missing units array' };
    }

    const updates: Array<{ unitNumber: string; coordinates: MapCoordinates; mapElementId?: string }> =
      [];

    for (const unit of parsed.units) {
      if (!unit.unit_number || !unit.map_coordinates) {
        continue;
      }

      if (unit.map_coordinates.type !== 'Polygon' || !unit.map_coordinates.points) {
        continue;
      }

      updates.push({
        unitNumber: unit.unit_number,
        coordinates: unit.map_coordinates,
        mapElementId: unit.map_element_id,
      });
    }

    if (updates.length === 0) {
      return { success: false, error: 'No valid units found in import data' };
    }

    // Get unit IDs for the updates
    const { data: units, error: fetchError } = await supabase
      .from('property_units')
      .select('id, unit_number')
      .in('unit_number', updates.map((u) => u.unitNumber));

    if (fetchError || !units) {
      return { success: false, error: fetchError?.message || 'Failed to fetch units' };
    }

    const unitIdMap = new Map(units.map((u) => [u.unit_number, u.id]));

    const bulkUpdates = updates
      .filter((u) => unitIdMap.has(u.unitNumber))
      .map((u) => ({
        unitId: unitIdMap.get(u.unitNumber)!,
        coordinates: u.coordinates,
        mapElementId: u.mapElementId,
      }));

    const result = await bulkUpdateMapCoordinates(bulkUpdates);

    trackEvent('admin_map_coordinates_imported', {
      imported_count: result.updatedCount || 0,
      total_in_file: parsed.units.length,
    });

    return result;
  } catch (error: unknown) {
    console.error('Error importing map coordinates:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: errorMessage };
  }
}

/**
 * Create a new site map polygon
 */
export async function createSiteMapPolygon(
  block: 'A' | 'B',
  polygonNumber: number,
  coordinates: MapCoordinates
): Promise<{ success: boolean; error?: string; data?: SiteMapPolygon }> {
  try {
    const { data, error } = await (supabase as any)
      .from('site_map_polygons')
      .insert({
        block,
        polygon_number: polygonNumber,
        map_coordinates: coordinates as unknown as Json,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating site map polygon:', error);
      return { success: false, error: error.message };
    }

    trackEvent('admin_site_map_polygon_created', {
      block,
      polygon_number: polygonNumber,
      point_count: coordinates.points?.length || 0,
    });

    return { success: true, data: data as SiteMapPolygon };
  } catch (error: unknown) {
    console.error('Error creating site map polygon:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: errorMessage };
  }
}

/**
 * Update an existing site map polygon
 */
export async function updateSiteMapPolygon(
  polygonId: string,
  coordinates: MapCoordinates
): Promise<{ success: boolean; error?: string; data?: SiteMapPolygon }> {
  try {
    const { data, error } = await (supabase as any)
      .from('site_map_polygons')
      .update({
        map_coordinates: coordinates as unknown as Json,
      })
      .eq('id', polygonId)
      .select()
      .single();

    if (error) {
      console.error('Error updating site map polygon:', error);
      return { success: false, error: error.message };
    }

    trackEvent('admin_site_map_polygon_updated', {
      polygon_id: polygonId,
      point_count: coordinates.points?.length || 0,
    });

    return { success: true, data: data as SiteMapPolygon };
  } catch (error: unknown) {
    console.error('Error updating site map polygon:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: errorMessage };
  }
}

/**
 * Get all site map polygons, optionally filtered by block
 */
export async function getSiteMapPolygons(
  block?: 'A' | 'B'
): Promise<{ data: SiteMapPolygon[] | null; error: string | null }> {
  try {
    let query = (supabase as any)
      .from('site_map_polygons')
      .select('*')
      .order('block')
      .order('polygon_number');

    if (block) {
      query = query.eq('block', block);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching site map polygons:', error);
      return { data: null, error: error.message };
    }

    return { data: (data || []) as SiteMapPolygon[], error: null };
  } catch (error: unknown) {
    console.error('Error fetching site map polygons:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { data: null, error: errorMessage };
  }
}

/**
 * Get a single site map polygon by ID
 */
export async function getSiteMapPolygonById(
  polygonId: string
): Promise<{ data: SiteMapPolygon | null; error: string | null }> {
  try {
    const { data, error } = await (supabase as any)
      .from('site_map_polygons')
      .select('*')
      .eq('id', polygonId)
      .single();

    if (error) {
      console.error('Error fetching site map polygon:', error);
      return { data: null, error: error.message };
    }

    return { data: data as SiteMapPolygon, error: null };
  } catch (error: unknown) {
    console.error('Error fetching site map polygon:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { data: null, error: errorMessage };
  }
}

/**
 * Delete a site map polygon
 */
export async function deleteSiteMapPolygon(
  polygonId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await (supabase as any)
      .from('site_map_polygons')
      .delete()
      .eq('id', polygonId);

    if (error) {
      console.error('Error deleting site map polygon:', error);
      return { success: false, error: error.message };
    }

    trackEvent('admin_site_map_polygon_deleted', {
      polygon_id: polygonId,
    });

    return { success: true };
  } catch (error: unknown) {
    console.error('Error deleting site map polygon:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: errorMessage };
  }
}

/**
 * Assign a unit to a site map polygon
 */
export async function assignUnitToSiteMapPolygon(
  unitId: string,
  polygonId: string
): Promise<{ success: boolean; error?: string; data?: PropertyUnit }> {
  try {
    const { data, error } = await (supabase as any)
      .from('property_units')
      .update({ site_map_polygon_id: polygonId })
      .eq('id', unitId)
      .select()
      .single();

    if (error) {
      console.error('Error assigning unit to site map polygon:', error);
      return { success: false, error: error.message };
    }

    trackEvent('admin_unit_assigned_to_polygon', {
      unit_id: unitId,
      unit_number: data.unit_number,
      polygon_id: polygonId,
    });

    return { success: true, data: data as PropertyUnit };
  } catch (error: unknown) {
    console.error('Error assigning unit to site map polygon:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: errorMessage };
  }
}

/**
 * Assign multiple units to a site map polygon
 */
export async function assignUnitsToSiteMapPolygon(
  unitIds: string[],
  polygonId: string
): Promise<{ success: boolean; error?: string; updatedCount?: number }> {
  try {
    const { data, error } = await (supabase as any)
      .from('property_units')
      .update({ site_map_polygon_id: polygonId })
      .in('id', unitIds)
      .select('id');

    if (error) {
      console.error('Error assigning units to site map polygon:', error);
      return { success: false, error: error.message };
    }

    trackEvent('admin_units_assigned_to_polygon', {
      unit_count: unitIds.length,
      polygon_id: polygonId,
      updated_count: data?.length || 0,
    });

    return { success: true, updatedCount: data?.length || 0 };
  } catch (error: unknown) {
    console.error('Error assigning units to site map polygon:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: errorMessage };
  }
}

/**
 * Get all units assigned to a site map polygon
 */
export async function getUnitsBySiteMapPolygon(
  polygonId: string
): Promise<{ data: PropertyUnit[] | null; error: string | null }> {
  try {
    const { data, error } = await (supabase as any)
      .from('property_units')
      .select('*')
      .eq('site_map_polygon_id', polygonId)
      .order('unit_number')
      .order('floor');

    if (error) {
      console.error('Error fetching units by site map polygon:', error);
      return { data: null, error: error.message };
    }

    return { data: (data || []) as PropertyUnit[], error: null };
  } catch (error: unknown) {
    console.error('Error fetching units by site map polygon:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { data: null, error: errorMessage };
  }
}

/**
 * Unassign a unit from its site map polygon
 */
export async function unassignUnitFromSiteMapPolygon(
  unitId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await (supabase as any)
      .from('property_units')
      .update({ site_map_polygon_id: null })
      .eq('id', unitId);

    if (error) {
      console.error('Error unassigning unit from site map polygon:', error);
      return { success: false, error: error.message };
    }

    trackEvent('admin_unit_unassigned_from_polygon', {
      unit_id: unitId,
    });

    return { success: true };
  } catch (error: unknown) {
    console.error('Error unassigning unit from site map polygon:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: errorMessage };
  }
}

